import { z } from 'zod';

export const mediaRoleSchema = z.enum([
  'HERO',
  'DETAIL',
  'CONTEXT',
  'SCALE',
  'SWATCH',
  'EDITORIAL',
]);

/**
 * Semua gambar pustaka disimpan di satu lokasi fisik `<env>/media/`;
 * pengelompokan dilakukan lewat folder di database, bukan lewat key.
 */
export const uploadTicketSchema = z.object({
  mimeType: z.enum(['image/webp', 'image/jpeg', 'image/png', 'image/avif', 'image/svg+xml']),
  /** Dipakai untuk menolak lebih awal; ukuran sebenarnya tetap diperiksa server. */
  sizeBytes: z.number().int().min(1).max(52_428_800),
});

export const mediaCommitSchema = z.object({
  key: z.string().min(8).max(200),
  role: mediaRoleSchema.default('DETAIL'),
  alt: z.string().max(240).default(''),
  /** Nama berkas saat diunggah, hanya untuk dibaca dan dicari di admin. */
  originalName: z.string().max(200).default(''),
  /** Folder tujuan; kosong berarti akar pustaka. */
  folderId: z.string().min(1).max(64).nullable().default(null),
});

/**
 * Tag pemakaian dihitung dari relasi, bukan diisi admin, jadi selalu sesuai
 * tempat gambar benar-benar dipakai. `unused` untuk mencari gambar yatim.
 * Imaji Studio (mockup, template, stiker) tidak termasuk: Studio menyimpan salinan
 * gambarnya sendiri (KEPUTUSAN 2A.5).
 */
export const MEDIA_USAGE_TAGS = [
  'product',
  'swatch',
  'category',
  'article',
  'page',
  'banner',
] as const;
export const mediaUsageFilterSchema = z.enum([...MEDIA_USAGE_TAGS, 'unused']);

export const mediaListQuerySchema = z.object({
  role: mediaRoleSchema.optional(),
  /** Cocokkan sebagian nama berkas atau teks alternatif. */
  q: z.string().trim().max(120).optional(),
  /**
   * Id folder, atau `root` untuk aset tanpa folder. Tidak diisi berarti semua
   * aset, dipakai pemilih media dan pencarian lintas folder.
   */
  folder: z.string().min(1).max(64).optional(),
  /** Tag pemakaian; seperti pencarian, berlaku lintas folder. */
  usage: mediaUsageFilterSchema.optional(),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(40),
});

export const mediaParamsSchema = z.object({ id: z.string().min(1).max(64) });

export const mediaUpdateSchema = z
  .object({
    role: mediaRoleSchema.optional(),
    alt: z.string().max(240).optional(),
    /** null memindahkan aset ke akar pustaka. */
    folderId: z.string().min(1).max(64).nullable().optional(),
  })
  .refine((d) => d.role !== undefined || d.alt !== undefined || d.folderId !== undefined, {
    message: 'Tidak ada perubahan yang dikirim',
  });

/** Garis miring dilarang supaya nama folder tidak dikira jalur. */
export const mediaFolderNameSchema = z
  .string()
  .trim()
  .min(1, 'Nama folder wajib diisi')
  .max(80, 'Nama folder maksimal 80 karakter')
  .refine((v) => !/[\\/<>]/.test(v), 'Nama folder tidak boleh berisi / \\ < >');

export const mediaFolderCreateSchema = z.object({
  name: mediaFolderNameSchema,
  parentId: z.string().min(1).max(64).nullable().default(null),
});

export const mediaFolderUpdateSchema = z
  .object({
    name: mediaFolderNameSchema.optional(),
    /** null memindahkan folder ke akar pustaka. */
    parentId: z.string().min(1).max(64).nullable().optional(),
  })
  .refine((d) => d.name !== undefined || d.parentId !== undefined, {
    message: 'Tidak ada perubahan yang dikirim',
  });

export type MediaRole = z.infer<typeof mediaRoleSchema>;
export type UploadTicketInput = z.infer<typeof uploadTicketSchema>;
export type MediaCommitInput = z.infer<typeof mediaCommitSchema>;
export type MediaListQuery = z.infer<typeof mediaListQuerySchema>;
export type MediaUpdateInput = z.infer<typeof mediaUpdateSchema>;
export type MediaFolderCreateInput = z.infer<typeof mediaFolderCreateSchema>;
export type MediaFolderUpdateInput = z.infer<typeof mediaFolderUpdateSchema>;

export type MediaUsageTag = (typeof MEDIA_USAGE_TAGS)[number];
export type MediaUsageFilter = z.infer<typeof mediaUsageFilterSchema>;
export type MediaUsage = Record<MediaUsageTag, number>;
